import axios from "axios";
import eventbus from "@eventbus";
import logger from "@helpers/logger";
import { router } from "@router/router";
import { useAppStore } from "@stores/appStore";
import { useUserStore } from "@stores/userStore";

function createApiClient(config = {}) {
    const instance = axios.create();
    const currentRequest = [];

    const getBaseUrl = (resource) => {
        if (typeof config.baseUrl === "function") {
            return config.baseUrl() + resource;
        }
        return (config.baseUrl || "/api") + resource;
    };

    const client = {
        AuthHeader() {
            // Only add token when it's set.
            const userStore = useUserStore();
            const token = userStore.getToken(config.tokenKey || "yhub");

            const headers = {
                "Accept": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            };

            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            instance.defaults.headers = headers;
        },

        deRegisterRequest(id) {
            // Quiet requests
            if (!id) {
                return;
            }

            if (!currentRequest.includes(id)) {
                logger.error(`Request not found: ${id}`);
            }

            const index = currentRequest.indexOf(id);
            currentRequest.splice(index, 1);

            if (currentRequest.length === 0) {
                eventbus.emit("loading", false);
            }
        },

        async delete(resource, slug = "") {
            const id = this.registerRequest("delete", resource);
            this.AuthHeader();

            try {
                const request = await instance.post(getBaseUrl(`${resource}/${slug}`), {
                    _method: "delete",
                });
                return this.handleResponse(request, id);
            } catch (error) {
                this.handleError(error, id);
            }
        },

        async get(resource, quiet = false) {
            this.AuthHeader();
            let id = null;

            if (!quiet) {
                id = this.registerRequest("get", resource);
            }

            try {
                const request = await instance.get(getBaseUrl(resource));
                return this.handleResponse(request, id);
            } catch (error) {
                this.handleError(error, id);
            }
        },

        async getFile(resource, slug = "") {
            this.AuthHeader();

            try {
                return await instance.get(getBaseUrl(`${resource}/${slug}`), {
                    responseType: "blob",
                });
            } catch (error) {
                this.handleError(error);
            }
        },

        // Request methods
        async getreport(resource, slug = "") {
            this.AuthHeader();

            try {
                const request = await instance.get(`${resource}/${slug}`, {
                    responseType: "blob",
                });

                return this.handleResponse(request);
            } catch (error) {
                this.handleError(error);
            }
        },

        async validate(resource, params, isNew = false, fieldName = "") {
            this.AuthHeader();

            const method = isNew ? "post" : "put";
            const url = getBaseUrl(resource);

            try {
                // We schieten de call rechtstreeks in via de Axios instance met de Precognition headers
                const request = await instance({
                    method: method,
                    url: url,
                    data: params,
                    headers: {
                        "Precognition": "true",
                        "Precognition-Validate-Only": fieldName,
                    },
                });

                // Als Laravel een 204 geeft, handelt handleResponse dit netjes af
                return this.handleResponse(request, null);
            } catch (error) {
                // Belangrijk: Laravel gooit een 422 bij een validatiefout.
                // Die willen we NIET door sturen naar de standaard handleError(),
                // want dan gooit hij een harde Error-crash. We willen de response teruggeven aan de Mixin.
                if (error.response && error.response.status === 422) {
                    return error.response;
                }

                // Andere fouten (zoals 401 of 500) mogen wel via de standaard handler
                this.handleError(error, null);
            }
        },

        handleError(error, id) {
            // Deregister request
            this.deRegisterRequest(id);

            // Unauthenticated -> no token or token expired.
            if (error.response.status === 401) {
                if (config.unauthenticatedAction === "logout") {
                    console.log("Unauthenticated, logging out...");
                    router.push({ name: "/auth/logout" });
                }
            } else {
                // Too many requests -- todo
                if (error.response.status === 429) {
                    eventbus.emit("notification", {
                        disableAutoHide: true,
                        message:
                            "Je gebruik heeft een limiet overtreden, wacht even voordat u weer verder gaat.",
                        title: "Gebruik limiet",
                        type: "warning",
                    });
                } else {
                    throw new Error(`[Err.] Api ${error}`);
                }
            }

            logger.error(`[Err.] Api ${error}`);
        },

        handleResponse(request, id) {
            // Check for redirects
            if (request.data.redirect) {
                this.redirect(request.data.redirect);
            }

            if (request.data.notification) {
                eventbus.emit("notification", request.data.notification);
            }

            // Check for version
            if (request.data.version) {
                eventbus.emit("set-version", request.data.version);
            }

            // Maintenance
            this.maintenance(request.data.maintenance);

            // Deregister request
            this.deRegisterRequest(id);

            // Return data to issuer.
            return request;
        },

        maintenance(maintenance) {
            if (maintenance) {
                eventbus.emit("maintenance-type", maintenance);
                eventbus.emit("maintenance-show", true);
            } else {
                eventbus.emit("maintenance-show", false);
            }
        },

        async post(resource, params, args) {
            const id = this.registerRequest("post", resource);
            this.AuthHeader();

            try {
                const request = await instance.post(getBaseUrl(`${resource}`), params, args);
                return this.handleResponse(request, id);
            } catch (error) {
                this.handleError(error, id);
            }
        },

        async put(resource, params) {
            const id = this.registerRequest("put", resource);
            this.AuthHeader();

            try {
                const request = await instance.put(getBaseUrl(`${resource}`), params);
                return this.handleResponse(request, id);
            } catch (error) {
                this.handleError(error, id);
            }
        },

        redirect(redirect) {
            let location = "/";

            // Redirect to location
            if (redirect.location) {
                location = redirect.location;
            }

            // Do redirect
            router.push(location);

            // Emit message if there is one
            if (redirect.message) {
                eventbus.emit("notification", redirect.message);
            }

            // Stop loading animation
            eventbus.emit("loading", false);
        },

        registerRequest(method, resources) {
            const stringId = `${method}.${resources}`;
            eventbus.emit("loading", true);

            if (currentRequest.includes(stringId)) {
                logger.error(`Request already in progress: [${method}] ${resources}`);
            }

            currentRequest.push(stringId);
            return stringId;
        },

        async saveObject(resource, params, id) {
            // if the id is not 'new' then we are updating an existing object
            if (id !== "new") {
                const url = resource.endsWith("/") ? `${resource}${id}` : `${resource}/${id}`;
                return this.put(url, params);
            }

            return this.post(resource, params);
        },
    };

    return client;
}

export const Api = createApiClient({
    tokenKey: "yhub",
    baseUrl: "/api",
    unauthenticatedAction: () => {
        const currentRoute = router.currentRoute.value;
        if (currentRoute.path.startsWith("/display")) {
            return null;
        }

        return "logout";
    },
});

export const DisplayApi = createApiClient({
    tokenKey: "display",
    baseUrl: "/api/connect/display",
    unauthenticatedAction: null,
});

export const LabHubApi = createApiClient({
    tokenKey: "labhub",
    baseUrl: () => {
        const appStore = useAppStore();
        return appStore.env.labhub_url + "/api";
    },
    unauthenticatedAction: null,
});

export default { Api, DisplayApi, LabHubApi };
